import React, { useContext, useMemo } from "react";

import { MenuItem } from "@mui/material";
import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format, zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { Languages } from "Models/languages";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import LanguagesService from "Services/languages.service";
import * as yup from "yup";

import { AutoSuggest, Input, Select } from "../../../../../../../../../components/Form";
import PhoneInput from "../../../../../../../../../components/Form/PhoneInput";
import {
  ALLOWED_IMAGE_EXTENSIONS,
  AvatarGeneric,
} from "../../../../../../../../../components/Generic/SettingsUserForm/components/AvatarGeneric";
import SkeletonComponent from "../../../../../../../../../components/Generic/Skeleton";
import IconWrapper from "../../../../../../../../../components/IconWrapper";
import { useAppState } from "../../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { Citizenship } from "../../../../../../../../../models/citizenship";
import { Country } from "../../../../../../../../../models/country";
import CitizenshipService from "../../../../../../../../../services/citizenship.service";
import CountryService from "../../../../../../../../../services/country.service";
import { LanguagesUsage, MembersType, Status } from "../../../../../../../../../util/enums";
import { formatCurrencyToInput } from "../../../../../../../../../util/formatCurrencyToInput";
import { currencyToNumber } from "../../../../../../../../../util/prices";
import {
  CitizenshipKeys,
  CountryKeys,
  LanguageKeys,
} from "../../../../../../../../../util/queries";
import { MemberContext } from "../../context";
import { memberGenderOptions } from "../Tabs/Details";
import { AvatarContainer, Wrapper } from "./styles";

const licenses = ["B", "BE", "C", "C1", "CE", "C1E", "D", "D1DE", "DE", "G"];

type Props = {
  errors?: yup.ValidationError[];
  onView?: boolean;
};

export default function Details({ errors = [], onView }: Props) {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { location } = useHistory<{ isView: boolean }>();
  const { member, handleUpdateMember, setMember } = useContext(MemberContext);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { data: translateList, isFetching: fetchingTranslateList } = useQuery({
    queryKey: LanguageKeys.getAll(["members"]),
    queryFn: async () => {
      const response = await LanguagesService.getAll({
        companyId: currentUser?.companyId,
        usage: LanguagesUsage.WEB,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data as Languages[];

      return [];
    },
    refetchOnWindowFocus: false,
  });

  const { data: citizenshipList, isFetching: fetchingCitizenshipList } = useQuery({
    queryKey: CitizenshipKeys.getAll([]),
    queryFn: async () => {
      const response = await CitizenshipService.getAll({
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    refetchOnWindowFocus: false,
  });

  const { data: countryList, isFetching: fetchingCountryList } = useQuery({
    queryKey: [CountryKeys.all],
    queryFn: async () => {
      const response = await CountryService.getAll({
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    refetchOnWindowFocus: false,
  });

  const getDateValue = (date: string) => {
    if (!date) return "";

    const currentTimezone =
      currentUser?.timezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;

    return format(utcToZonedTime(date, currentTimezone), "yyyy-MM-dd");
  };

  const setDateValue = (dateValue: string, attribute: string) => {
    if (!dateValue) return;

    const dateString = dateValue.includes("T") ? dateValue.split("T")[0] : dateValue;

    const currentTimezone =
      currentUser?.timezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;

    const dateToAct = zonedTimeToUtc(dateString, currentTimezone);

    if (dateToAct) {
      handleUpdateMember(attribute, dateToAct.toISOString());
    }
  };

  const regexToPrice = useMemo(() => {
    return /^\d+(\.(\d{1,2})?)?$/;
  }, [currentUser?.company?.currency]);

  return (
    <Wrapper>
      <Grid container direction="row" spacing={2}>
        <AvatarContainer item xs={12} md={3}>
          <AvatarGeneric
            allowedImageExtensions={ALLOWED_IMAGE_EXTENSIONS}
            name="picture"
            data-cy="avatar"
            picture={member?.picture?.mediaLink || ""}
            onChangePicture={(value) => {
              if (value) {
                handleUpdateMember(
                  "picture",
                  member?.picture?.mediaLink
                    ? {
                        mediaLink: value,
                        id: member?.picture?.id,
                        name: member?.picture?.name,
                      }
                    : { mediaLink: value }
                );
              } else {
                handleUpdateMember("picture", null);
              }
            }}
            isOnViewMode={location?.state?.isView || onView}
          />
        </AvatarContainer>
        <Grid item container direction="row" xs={12} md={9} spacing={3}>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md>
              <Input
                disabled={location?.state?.isView || onView}
                label={translate("nickname")}
                value={member?.nickname}
                placeholder="Type here..."
                onChange={(event) => handleUpdateMember("nickname", event.target.value)}
                error={fieldHasError("nickname")}
                errorMessage={getErrorByField("nickname")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                disabled={location?.state?.isView || onView}
                label={translate("firstName")}
                required
                value={member?.firstName}
                onChange={(event) => handleUpdateMember("firstName", event.target.value)}
                error={fieldHasError("firstName")}
                errorMessage={getErrorByField("firstName")}
                placeholder={translate("exampleAyrton")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                disabled={location?.state?.isView || onView}
                label={translate("lastName")}
                required
                value={member?.lastName}
                onChange={(event) => handleUpdateMember("lastName", event.target.value)}
                error={fieldHasError("lastName")}
                errorMessage={getErrorByField("lastName")}
                placeholder={translate("exampleSenna")}
              />
            </Grid>
            <Grid item xs={12} md>
              <Select
                disabled={location?.state?.isView || onView}
                label={translate("status")}
                required
                value={member?.status}
                onChange={(event) => handleUpdateMember("status", event.target.value)}
                error={fieldHasError("status")}
                errorMessage={getErrorByField("status")}
              >
                <MenuItem value={Status.ACTIVE}>{Status.ACTIVE}</MenuItem>
                <MenuItem value={Status.INACTIVE}>{Status.INACTIVE}</MenuItem>
              </Select>
            </Grid>
          </Grid>

          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <Input
                disabled={location?.state?.isView || onView}
                label={translate("email")}
                required={member?.typeOfMember?.toLowerCase() === MembersType.USER.toLowerCase()}
                placeholder="mail@mail.com"
                value={member?.email}
                onChange={(event) => handleUpdateMember("email", event.target.value)}
                error={fieldHasError("email")}
                errorMessage={getErrorByField("email")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PhoneInput
                disabled={location?.state?.isView || onView}
                label={translate("mobile")}
                required
                value={member?.mobile}
                onChange={(value) => handleUpdateMember("mobile", value)}
                placeholder="0000 000 000"
                error={fieldHasError("mobile")}
                errorMessage={getErrorByField("mobile")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PhoneInput
                disabled={location?.state?.isView}
                label={translate("phone")}
                value={member?.phone}
                onChange={(value) => handleUpdateMember("phone", value)}
                placeholder="0000 000 000"
              />
            </Grid>
          </Grid>

          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <Input
                disabled={location?.state?.isView || onView}
                label={translate("dateOfBirth")}
                type="date"
                value={member?.dateOfBirth ? getDateValue(member?.dateOfBirth) : ""}
                onChange={(event) => setDateValue(event.target.value, "dateOfBirth")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                disabled={
                  location?.state?.isView ||
                  onView ||
                  (!!member?.startEmploymentDate && member?.status === Status.ACTIVE && !!member.id)
                }
                label={translate("startEmploymentDate")}
                type="date"
                value={member?.startEmploymentDate ? getDateValue(member?.startEmploymentDate) : ""}
                onChange={(event) => setDateValue(event?.target.value, "startEmploymentDate")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                disabled={location?.state?.isView || onView}
                label={translate("endEmploymentDate")}
                type="date"
                value={member?.endEmploymentDate ? getDateValue(member?.endEmploymentDate) : ""}
                onChange={(event) => setDateValue(event?.target.value, "endEmploymentDate")}
              />
            </Grid>
          </Grid>

          <Grid item container xs={12} spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <SkeletonComponent isLoading={fetchingTranslateList}>
                <Select
                  disabled={location?.state?.isView || onView}
                  label={translate("language")}
                  value={member?.languageId}
                  onChange={(event) => handleUpdateMember("languageId", Number(event.target.value))}
                  emptyPlaceholder={translate("noLanguageAvailable")}
                  placeholder={translate("select")}
                >
                  {translateList?.map((translate) => (
                    <MenuItem key={translate.id} value={translate.id}>
                      {translate.name}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </Grid>
            <Grid item xs={12} md={4}>
              <SkeletonComponent isLoading={fetchingCountryList}>
                <AutoSuggest
                  disabled={location?.state?.isView || onView}
                  label={translate("country")}
                  placeholder={translate("select")}
                  value={member?.countryId}
                  inputValue={member?.country?.name}
                  options={countryList || []}
                  dataCy="timezone"
                  getOptionLabel={(country: Country) => country?.name || ""}
                  getOptionSelected={(option: Country, value: Country) =>
                    option && value && option.id === value.id
                  }
                  onChange={(value: Country) =>
                    setMember({ ...member, country: value, countryId: Number(value?.id) })
                  }
                />
              </SkeletonComponent>
            </Grid>
            <Grid item xs={12} md={4}>
              <SkeletonComponent isLoading={fetchingCitizenshipList}>
                <AutoSuggest
                  disabled={location?.state?.isView || onView}
                  label={translate("citizenship")}
                  placeholder={translate("select")}
                  value={member?.citizenshipId}
                  inputValue={member?.citizenship?.name}
                  options={citizenshipList}
                  getOptionLabel={(citizenship: Citizenship) => citizenship?.name}
                  getOptionSelected={(option: Citizenship, value: Citizenship) =>
                    option && value && option.id === value.id
                  }
                  onChange={(value: Citizenship) =>
                    setMember({ ...member, citizenship: value, citizenshipId: Number(value?.id) })
                  }
                />
              </SkeletonComponent>
            </Grid>
          </Grid>

          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <Select
                disabled={location?.state?.isView || onView}
                label={translate("license")}
                value={member?.license}
                onChange={(event) => handleUpdateMember("license", event.target.value)}
                placeholder={translate("select")}
              >
                {licenses?.map((license) => (
                  <MenuItem key={`license-${license}`} value={license}>
                    {translate(`license`)} {license}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                disabled={location?.state?.isView || onView}
                label={translate("jobTitle")}
                value={member?.jobTitle}
                onChange={(event) => handleUpdateMember("jobTitle", event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Select
                disabled={location?.state?.isView || onView}
                label={translate("gender")}
                value={member?.gender}
                onChange={(event) => handleUpdateMember("gender", event.target.value)}
              >
                {memberGenderOptions.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {translate(label)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={4}>
              <Input
                label={translate("rate")}
                disabled={location?.state?.isView || onView}
                value={member?.ratePerHour}
                onChange={(event) => {
                  if (
                    !event.target.value ||
                    (event.target.value && regexToPrice.test(event.target.value))
                  )
                    handleUpdateMember("ratePerHour", event.target.value);
                }}
                onBlur={(event) => {
                  handleUpdateMember(
                    "ratePerHour",
                    formatCurrencyToInput(currencyToNumber(event.target.value))
                  );
                }}
                placeholder={"0.00"}
                leftIcon={
                  <IconWrapper>{currentUser?.company?.currency?.symbol || "$"}</IconWrapper>
                }
                rightIcon={<IconWrapper>/{translate("hour")}</IconWrapper>}
                inputTextAlign={"start"}
                error={fieldHasError("ratePerHour")}
                errorMessage={getErrorByField("ratePerHour")}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Wrapper>
  );
}
